/**
 * ET-109 — sessionStorage suppression for the engagement-stall CTA modal.
 * Session- and device-scoped per treatment (Owen Hall, 2026-07-08): the modal
 * must never re-show for the same treatment page once it has been shown,
 * dismissed, or superseded by a direct page CTA click during the current
 * session. A single `suppressed` flag covers all three rules; `reason` is
 * kept only for debugging, not branched on by any caller.
 */
import { getSessionJSONItem, setSessionJSONItem } from "~/lib/utils/utils";

export type EngagementStallSuppressReason =
  | "shown"
  | "dismissed"
  | "cta_clicked"
  | "page_cta_clicked";

interface EngagementStallState {
  suppressed: true;
  reason: EngagementStallSuppressReason;
}

function storageKey(treatmentSlug: string): string {
  return `hec.engagement-stall.${treatmentSlug}`;
}

/**
 * Reads suppression state for a treatment. Fails soft to `false` on a missing
 * key, corrupt JSON (`getSessionJSONItem` calls `JSON.parse` with no guard),
 * or a sessionStorage read throwing (Safari private mode) — a storage failure
 * must never block the modal's own suppression logic mid-session, it should
 * just behave as if nothing was suppressed yet.
 */
export function isEngagementStallSuppressed(treatmentSlug: string): boolean {
  try {
    const state = getSessionJSONItem(storageKey(treatmentSlug)) as
      | EngagementStallState
      | undefined;
    return state?.suppressed === true;
  } catch {
    return false;
  }
}

/**
 * Marks a treatment's engagement-stall modal as suppressed for the rest of
 * the session/device. Swallows write failures (quota exceeded, Safari private
 * mode) — a failed suppression write should never surface as a user-visible
 * error, it just risks the modal showing again, which is the pre-ET-109
 * behavior anyway.
 */
export function suppressEngagementStall(
  treatmentSlug: string,
  reason: EngagementStallSuppressReason,
): void {
  try {
    setSessionJSONItem(storageKey(treatmentSlug), {
      suppressed: true,
      reason,
    } satisfies EngagementStallState);
  } catch {
    // no-op — see doc comment above.
  }
}
